import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/superbuy';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMenu = useCallback(() => setMobileMenuOpen(false), []);

  const scrollToSection = (id: string) => {
    closeMenu();
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 150);
  };

  // On non-homepage pages, always show the solid navbar since there's no hero background
  const showSolid = scrolled || mobileMenuOpen || !isHomePage;
  const textColor = showSolid ? 'text-foreground-700' : 'text-white/90';
  const textHover = showSolid ? 'hover:text-foreground-950' : 'hover:text-white';
  const menuIconColor = showSolid ? 'text-foreground-800' : 'text-white';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          showSolid
            ? 'bg-background-50/95 backdrop-blur-md'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between h-16">
          <Link
            to="/"
            onClick={closeMenu}
            className="flex items-center gap-2 text-foreground-950 font-heading text-lg font-semibold whitespace-nowrap cursor-pointer"
          >
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-leaf-line text-primary-500 text-xl"></i>
            </div>
            superbuysheet.lat
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              onClick={closeMenu}
              className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${textColor} ${textHover}`}
            >
              Home
            </Link>
            {isHomePage ? (
              <button
                onClick={() => scrollToSection('categories')}
                className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${textColor} ${textHover}`}
              >
                Categories
              </button>
            ) : (
              <Link
                to="/#categories"
                className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${textColor} ${textHover}`}
              >
                Categories
              </Link>
            )}
            {isHomePage ? (
              <button
                onClick={() => scrollToSection('guides')}
                className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${textColor} ${textHover}`}
              >
                Guides
              </button>
            ) : (
              <Link
                to="/#guides"
                className={`text-sm whitespace-nowrap cursor-pointer transition-colors ${textColor} ${textHover}`}
              >
                Guides
              </Link>
            )}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300"
            >
              Buy It Now
            </a>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-accent-500 text-background-50 dark:text-foreground-950 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="w-9 h-9 flex items-center justify-center cursor-pointer rounded-lg hover:bg-foreground-100/10 transition-colors"
              aria-label="Open menu"
            >
              <i className={`ri-menu-line text-xl ${menuIconColor}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={closeMenu}
          />

          <div className="absolute right-0 top-0 bottom-0 w-[85vw] max-w-[380px] bg-background-50 overflow-y-auto">
            <div className="flex items-center justify-between px-6 h-16 border-b border-background-200">
              <Link
                to="/"
                onClick={closeMenu}
                className="flex items-center gap-2 text-foreground-950 font-heading text-base font-semibold whitespace-nowrap cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-leaf-line text-primary-500 text-lg"></i>
                </div>
                superbuysheet
              </Link>
              <button
                onClick={closeMenu}
                className="w-9 h-9 flex items-center justify-center cursor-pointer rounded-lg hover:bg-background-100 transition-colors"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-xl text-foreground-700"></i>
              </button>
            </div>

            <div className="px-6 py-5">
              <div className="space-y-2 mb-8">
                <Link
                  to="/"
                  onClick={closeMenu}
                  className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors flex items-center gap-3"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-home-4-line text-primary-500"></i>
                  </div>
                  Home
                </Link>
                {isHomePage ? (
                  <button
                    onClick={() => scrollToSection('categories')}
                    className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors flex items-center gap-3"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-grid-line text-primary-500"></i>
                    </div>
                    Categories
                  </button>
                ) : (
                  <Link
                    to="/#categories"
                    onClick={closeMenu}
                    className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors flex items-center gap-3"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-grid-line text-primary-500"></i>
                    </div>
                    Categories
                  </Link>
                )}
                {isHomePage ? (
                  <button
                    onClick={() => scrollToSection('guides')}
                    className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors flex items-center gap-3"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-book-open-line text-primary-500"></i>
                    </div>
                    Guides
                  </button>
                ) : (
                  <Link
                    to="/#guides"
                    onClick={closeMenu}
                    className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer transition-colors flex items-center gap-3"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-book-open-line text-primary-500"></i>
                    </div>
                    Guides
                  </Link>
                )}
              </div>

              <div className="mb-8">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3 px-4">
                  Product Categories
                </p>
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMenu}
                      className="w-full text-left px-4 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors flex items-center gap-3"
                    >
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-primary-400 text-sm`}></i>
                      </div>
                      <span>{cat.name}</span>
                      <div className="w-4 h-4 flex items-center justify-center ml-auto flex-shrink-0">
                        <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3 px-4 pb-8">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMenu}
                  className="bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 px-5 py-3 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 text-center flex items-center justify-center gap-2"
                >
                  Superbuy Sheet
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMenu}
                  className="bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 px-5 py-3 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 text-center"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}